import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Button, Input, Label, Icon, Form, Popup } from 'semantic-ui-react';

export const MAX_LOCATIONS = 10;
const LOOKUP_DELAY_MS = 400;

const BLUE = '#1e5b99';
const inputStyle = { marginBottom: 10 };
const chipsStyle = { display: 'flex', flexWrap: 'wrap', alignItems: 'center' };
const chipStyle = { marginTop: 4, marginRight: 4, marginLeft: 0, border: 'none', background: '#e8f1fb', color: BLUE };
const chipLinkStyle = { color: BLUE, opacity: 1 };
const chipIconStyle = { color: BLUE };
const countStyle = { marginLeft: 'auto', paddingLeft: 12, color: 'rgba(0,0,0,.4)', fontSize: '0.92857143em' };
const headerStyle = { display: 'flex', alignItems: 'center', marginBottom: 4 };
const labelStyle = { fontSize: '0.92857143em', fontWeight: 700, color: 'rgba(0,0,0,.87)' };
const previewStyle = {
  display: 'flex',
  alignItems: 'center',
  margin: '0 0 12px',
  padding: '12px 14px',
  background: '#fff',
  border: '1px solid rgba(34,36,38,.12)',
  borderLeft: `3px solid ${BLUE}`,
  borderRadius: 4,
  boxShadow: '0 2px 6px rgba(34,36,38,.08)',
};
const previewBadgeStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: 40,
  height: 40,
  marginRight: 12,
  borderRadius: '50%',
  background: '#e8f1fb',
  color: BLUE,
};
const previewBadgeIconStyle = { margin: 0 };
const previewBodyStyle = { flex: 1, minWidth: 0 };
const previewNameStyle = { fontWeight: 700, fontSize: '1.07em', color: 'rgba(0,0,0,.87)' };
const previewOpenStyle = { marginLeft: 6, color: 'rgba(0,0,0,.4)' };
const previewDetailStyle = {
  display: 'flex',
  alignItems: 'center',
  marginTop: 4,
  color: 'rgba(0,0,0,.55)',
  fontSize: '0.92857143em',
};
const categoryStyle = {
  marginRight: 8,
  padding: '2px 8px',
  borderRadius: 10,
  background: '#f1f2f3',
  color: 'rgba(0,0,0,.65)',
  fontSize: '0.92857143em',
};
const previewActionsStyle = { display: 'flex', alignItems: 'center', flexShrink: 0, marginLeft: 12 };
const cancelStyle = { background: 'transparent', boxShadow: 'none', color: 'rgba(0,0,0,.55)', fontWeight: 400 };

export const parseLocationId = value => {
  const text = _.trim(value);
  if (/^\d+$/.test(text)) return text;
  const match = text.match(/instagram\.com\/explore\/locations\/(\d+)/i);
  return match ? match[1] : null;
};

export const locationUrl = ({ id, slug }) =>
  `https://www.instagram.com/explore/locations/${id}/${slug ? `${slug}/` : ''}`;

export const formatPosts = mediaCount =>
  (_.isNumber(mediaCount) ? `${mediaCount.toLocaleString('en-GB')} ${mediaCount === 1 ? 'post' : 'posts'}` : null);

const HelpPopup = () => <Popup
  content={`Instagram posts and stories tagged at any of these locations are added to the campaign.
    Paste a location link from Instagram, check the preview, then add it. Up to ${MAX_LOCATIONS} locations.`}
  trigger={<Icon name="help circle" className="circular" />}
/>;

export default class InstagramLocationsInput extends Component {
  constructor(props) {
    super(props);
    this.state = { value: '', isLookingUp: false, message: null, preview: null };
    this.lookupRequest = 0;
    this.lookupLater = _.debounce(this.lookup, LOOKUP_DELAY_MS);
  }

  componentWillUnmount() {
    this.lookupLater.cancel();
    this.lookupRequest += 1;
  }

  // Looks the location up as soon as a valid link or id has been pasted.
  onInputChange = (e, { value }) => {
    this.lookupLater.cancel();
    this.lookupRequest += 1;
    this.setState({ value, message: null, preview: null, isLookingUp: false });
    if (parseLocationId(value)) this.lookupLater(value, false);
  }

  onKeyPress = e => {
    if (e.key !== 'Enter') return;
    // Stops Enter from submitting a surrounding form.
    e.preventDefault();
    if (this.state.preview) {
      this.onConfirm();
    } else {
      this.onFind();
    }
  }

  onFind = e => {
    if (e) e.preventDefault();
    this.lookupLater.cancel();
    this.lookup(this.state.value, true);
  }

  onConfirm = e => {
    if (e) e.preventDefault();
    const { locations, isSaving, onChange } = this.props;
    const { preview } = this.state;
    if (!preview || isSaving || this.isFull()) return;
    if (_.some(locations, { id: preview.id })) {
      this.setState({ preview: null, message: 'That location has already been added.' });
      return;
    }
    this.setState({ value: '', preview: null, message: null });
    onChange([...locations, _.pick(preview, ['id', 'name', 'slug'])]);
  }

  onCancel = e => {
    if (e) e.preventDefault();
    this.setState({ value: '', preview: null, message: null });
  }

  onDelete = id => {
    const { locations, isSaving, onChange } = this.props;
    if (isSaving) return;
    this.setState({ message: null });
    onChange(_.reject(locations, { id }));
  }

  // explicit: the user asked for it, so explain why nothing was looked up.
  lookup = (value, explicit) => {
    const { locations, onLookup } = this.props;
    if (!_.trim(value) || this.isFull()) return null;

    const id = parseLocationId(value);
    if (!id) {
      if (explicit) this.setState({ message: 'Paste a link like instagram.com/explore/locations/111106416225578/…' });
      return null;
    }
    if (_.some(locations, { id })) {
      this.setState({ message: 'That location has already been added.' });
      return null;
    }

    this.lookupRequest += 1;
    const request = this.lookupRequest;
    const isCurrent = () => request === this.lookupRequest;
    this.setState({ isLookingUp: true, message: null, preview: null });
    return onLookup(id)
      .then(location => {
        if (!isCurrent()) return;
        if (!location) {
          this.setState({ isLookingUp: false, message: 'We couldn’t find that location on Instagram.' });
          return;
        }
        this.setState({ isLookingUp: false, preview: location });
      })
      .catch(() => {
        if (!isCurrent()) return;
        this.setState({ isLookingUp: false, message: 'Could not look up the location. Please try again.' });
      });
  }

  isFull = () => _.size(this.props.locations) >= MAX_LOCATIONS;

  renderPreview = preview => (
    <div style={previewStyle} className="location-preview">
      <div style={previewBadgeStyle}>
        <Icon name="marker" size="large" style={previewBadgeIconStyle} />
      </div>
      <div style={previewBodyStyle}>
        <div style={previewNameStyle}>
          {preview.name}
          <a
            href={locationUrl(preview)}
            target="_blank"
            rel="noopener noreferrer"
            title="Open on Instagram"
            style={previewOpenStyle}
          >
            <Icon name="external" />
          </a>
        </div>
        <div style={previewDetailStyle}>
          {preview.category && <span style={categoryStyle}>{preview.category}</span>}
          {formatPosts(preview.mediaCount)}
        </div>
      </div>
      <div style={previewActionsStyle}>
        <Button type="button" size="small" content="Cancel" style={cancelStyle} onClick={this.onCancel} />
        <Button
          type="button"
          primary
          size="small"
          icon="plus"
          content="Add"
          loading={this.props.isSaving}
          disabled={this.props.isSaving}
          onClick={this.onConfirm}
        />
      </div>
    </div>
  );

  renderChip = location => (
    <Label key={location.id} size="large" style={chipStyle}>
      <Icon name="marker" style={chipIconStyle} />
      <a href={locationUrl(location)} target="_blank" rel="noopener noreferrer" style={chipLinkStyle}>
        {location.name}
      </a>
      <Icon name="close" link style={chipIconStyle} onClick={() => this.onDelete(location.id)} />
    </Label>
  );

  render() {
    const { locations, error, hideLabel } = this.props;
    const { value, isLookingUp, message, preview } = this.state;
    const isFull = this.isFull();
    const shownMessage = error || message;

    return (
      <Form.Field>
        {
          !hideLabel &&
          <div style={headerStyle}>
            <label htmlFor="campaign-locations" style={labelStyle}>
              Tracked locations <HelpPopup />
            </label>
          </div>
        }
        {
          shownMessage ? (
            <Label basic color="red" pointing="below">
              {shownMessage}
            </Label>) : null
        }
        <Input
          id="campaign-locations"
          fluid
          icon="instagram"
          iconPosition="left"
          loading={isLookingUp}
          disabled={isFull}
          onChange={this.onInputChange}
          onKeyPress={this.onKeyPress}
          placeholder={isFull
            ? `You can add up to ${MAX_LOCATIONS} locations`
            : 'Paste an Instagram location link, e.g. instagram.com/explore/locations/…'}
          value={value}
          style={inputStyle}
        />
        { preview && this.renderPreview(preview) }
        {
          !_.isEmpty(locations) &&
          <div style={chipsStyle}>
            { _.map(locations, this.renderChip) }
            <span style={countStyle}>{_.size(locations)} of {MAX_LOCATIONS}</span>
          </div>
        }
      </Form.Field>
    );
  }
}

InstagramLocationsInput.propTypes = {
  locations: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    slug: PropTypes.string,
  })),
  onChange: PropTypes.func.isRequired,
  onLookup: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
  error: PropTypes.string,
  hideLabel: PropTypes.bool,
};

InstagramLocationsInput.defaultProps = {
  locations: [],
  isSaving: false,
  error: null,
  hideLabel: false,
};



/** WEBPACK FOOTER **
 ** ./src/components/instagram-locations-input/index.js
 **/